function hasVisibleLabel(label) {
  return label !== null && label !== undefined && label !== false && label !== '';
}

function normalizeOrientation(orientation) {
  return orientation === 'vertical' ? 'vertical' : 'horizontal';
}

function normalizeRequestedLayout(requestedLayout) {
  return requestedLayout === 'inline' || requestedLayout === 'stacked'
    ? requestedLayout
    : 'auto';
}

export function isStackedEditableSliderLayout({ mode, valueDisplay } = {}) {
  return mode === 'stacked' && valueDisplay === 'editable';
}

export function resolveControlLayout({
  type,
  label,
  orientation = 'horizontal',
  requestedLayout = 'auto',
  valueDisplay,
  thumbStyle,
  fillMode,
}) {
  const resolvedOrientation = normalizeOrientation(orientation);
  const normalizedRequestedLayout = normalizeRequestedLayout(requestedLayout);
  const visibleLabel = hasVisibleLabel(label);

  if (type === 'slider') {
    const explicitValueDisplay = valueDisplay !== undefined;
    const requestedValueDisplay = valueDisplay ?? 'inline';
    const shouldAutoInline = normalizedRequestedLayout === 'auto'
      && resolvedOrientation === 'horizontal'
      && visibleLabel
      && !explicitValueDisplay;
    const mode = normalizedRequestedLayout === 'inline' || shouldAutoInline
      ? 'inline'
      : 'stacked';
    const resolvedValueDisplay = mode === 'inline' && !explicitValueDisplay
      ? 'editable'
      : requestedValueDisplay;

    return {
      mode,
      orientation: resolvedOrientation,
      bands: mode === 'inline'
        ? [['label', 'track', 'value']]
        : isStackedEditableSliderLayout({ mode, valueDisplay: resolvedValueDisplay })
          ? [['label'], ['track', 'value']]
          : [['label', 'value'], ['track']],
      valueDisplay: resolvedValueDisplay,
      thumbStyle: thumbStyle || (mode === 'inline' ? 'outline' : 'solid'),
      fillMode: fillMode || (mode === 'inline' ? 'none' : 'auto'),
    };
  }

  if (type === 'toggle') {
    return {
      mode: 'inline',
      orientation: resolvedOrientation,
      bands: [['control', 'label']],
      valueDisplay,
      thumbStyle,
      fillMode,
    };
  }

  if (type === 'reference-chip') {
    return {
      mode: 'inline',
      orientation: resolvedOrientation,
      bands: [['thumb', 'label']],
      valueDisplay,
      thumbStyle,
      fillMode,
    };
  }

  return {
    mode: 'stacked',
    orientation: resolvedOrientation,
    bands: visibleLabel ? [['label'], ['control']] : [['control']],
    valueDisplay,
    thumbStyle,
    fillMode,
  };
}

function resolveSlotHeight(slotHeights, slot) {
  const height = Number(slotHeights?.[slot]);
  return Number.isFinite(height) ? Math.max(0, height) : 0;
}

export function measureControlLayoutHeight(
  { bands },
  { slotHeights, bandGap = 0, marginBlockEnd = 0, minHeight = 0 },
) {
  const resolvedBands = Array.isArray(bands) ? bands : [];
  const bandsHeight = resolvedBands.reduce((sum, band) => {
    const slots = Array.isArray(band) ? band : [];
    const bandHeight = slots.reduce(
      (maximum, slot) => Math.max(maximum, resolveSlotHeight(slotHeights, slot)),
      0,
    );
    return sum + bandHeight;
  }, 0);
  const gapsHeight = Math.max(0, resolvedBands.length - 1) * Math.max(0, Number(bandGap) || 0);
  const measuredHeight = bandsHeight
    + gapsHeight
    + Math.max(0, Number(marginBlockEnd) || 0);

  return Math.max(measuredHeight, Math.max(0, Number(minHeight) || 0));
}

// X coordinates are relative to the row content box; callers add row padding.
export function resolveSliderInlineColumnLayout({
  availableWidth,
  gap,
  labelMinWidth,
  labelPreferredWidth,
  trackMinWidth,
  valueWidth,
}) {
  const availableColumnsWidth = Math.max(0, availableWidth - gap * 2);
  const labelWidth = Math.max(
    labelMinWidth,
    Math.min(labelPreferredWidth, availableColumnsWidth - valueWidth - trackMinWidth),
  );
  const trackX = labelWidth + gap;
  const trackWidth = Math.max(0, availableColumnsWidth - labelWidth - valueWidth);
  const valueX = trackX + trackWidth + gap;

  return {
    label: { x: 0, width: labelWidth },
    track: { x: trackX, width: trackWidth },
    value: { x: valueX, width: valueWidth },
  };
}
